import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, LucideIcon } from "lucide-react";

type TierCode = "free" | "starter" | "pro" | "expert";
type BillingInterval = "monthly" | "yearly";

interface Tier {
  code: TierCode;
  name: string;
  displayName: string;
  description: string;
  monthlyPriceCents: number;
  yearlyPriceCents: number;
  creditRateCents: number;
}

interface PlanCardProps {
  tier: Tier;
  icon: LucideIcon;
  billingInterval: BillingInterval;
  isRecommended: boolean;
  isPopular: boolean;
  isCurrent: boolean;
  isProcessing: boolean;
  hasCompany: boolean;
  currentTierPrice: number;
  currentBillingPeriod?: BillingInterval; // Add current billing period
  onSelectPlan: () => void;
  isAdmin: boolean;
  needsSubscription?: boolean; // True if user has no active subscription (blocked or canceled)
  canReactivate?: boolean; // True if current plan is scheduled for cancellation and can be reactivated
}

export function PlanCard({
  tier,
  icon: Icon,
  billingInterval,
  isRecommended,
  isPopular,
  isCurrent,
  isProcessing,
  hasCompany,
  currentTierPrice,
  currentBillingPeriod,
  onSelectPlan,
  isAdmin,
  needsSubscription = false,
  canReactivate = false,
}: PlanCardProps) {
  const formatPrice = (priceInCents: number): string => {
    if (priceInCents === 0) return "Free";
    return `$${(priceInCents / 100).toFixed(0)}`;
  };

  const getTierPrice = (): number => {
    return billingInterval === "monthly" ? tier.monthlyPriceCents : tier.yearlyPriceCents;
  };

  const formatUsagePrice = (priceInCents: number): string => {
    if (priceInCents === 0) return "$0";

    if (priceInCents < 100) {
      const centsStr = priceInCents % 1 === 0 ? priceInCents.toFixed(0) : priceInCents.toString();
      return `${centsStr}¢`;
    }

    const price = priceInCents / 100;
    if (priceInCents % 100 === 0) {
      return `$${price.toFixed(0)}`;
    }
    return `$${price.toFixed(2)}`;
  };

  const price = getTierPrice();
  
  // Compare prices aligned to the selected billing interval to correctly determine upgrade/downgrade
  // When comparing across billing periods, use the price for the interval being viewed
  const newPriceForComparison = billingInterval === 'yearly'
    ? tier.yearlyPriceCents
    : tier.monthlyPriceCents;
  
  // currentTierPrice represents the price of the current tier in the current billing period
  // Compare apples-to-apples: if we're viewing yearly plans, compare yearly prices
  const isUpgrade = newPriceForComparison > currentTierPrice;
  
  // Check if this is a billing period change (monthly ↔ yearly)
  const isBillingPeriodChange = currentBillingPeriod && currentBillingPeriod !== billingInterval;
  const isYearlyUpgrade = currentBillingPeriod === 'monthly' && billingInterval === 'yearly';

  return (
    <Card
      className={`relative flex flex-col transition-all ${isRecommended
        ? "border-primary shadow-xl ring-2 ring-primary/20 scale-105"
        : ""
        } ${isCurrent ? "border-green-500 bg-green-50/5" : ""}`}
    >
      {/* Current Plan Badge - takes priority over Popular */}
      {isCurrent ? (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <Badge className="bg-green-600 px-4 py-1 text-xs font-semibold">
            Current Plan
          </Badge>
        </div>
      ) : isPopular ? (
        /* Popular Badge - only show if not current plan */
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary px-4 py-1 text-xs font-semibold">
            Most Popular
          </Badge>
        </div>
      ) : null}

      <CardHeader className="pb-8 space-y-4">
        {/* Icon and Name */}
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${isRecommended ? "bg-primary/10" : "bg-muted"
            }`}>
            <Icon className={`h-7 w-7 ${isRecommended ? "text-primary" : "text-muted-foreground"
              }`} />
          </div>
          <div>
            <CardTitle className="text-2xl">{tier.name}</CardTitle>
          </div>
        </div>

        <CardDescription className="text-sm min-h-[40px]">
          {tier.description}
        </CardDescription>

        {/* Pricing */}
        <div className="pt-2">
          <div className="flex items-baseline gap-2">
            <span className="text-5xl font-bold">
              {formatPrice(price)}
            </span>
            {price > 0 && (
              <span className="text-muted-foreground text-lg">
                /{billingInterval === "monthly" ? "mo" : "yr"}
              </span>
            )}
          </div>
          {tier.creditRateCents > 0 && (
            <div className="text-xs text-muted-foreground mt-3 border-t pt-3 space-y-1">
              <div className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                <span>Comparison: {formatUsagePrice(tier.creditRateCents * 25)} per page</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-muted-foreground" />
                <span>Supplement: {formatUsagePrice(tier.creditRateCents * 5)} per line item</span>
              </div>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col justify-end">
        {/* CTA Button */}
        <Button
          className="w-full"
          size="lg"
          variant={isRecommended && !isCurrent ? "default" : "outline"}
          disabled={!hasCompany || (isCurrent && !canReactivate) || isProcessing || !isAdmin}
          onClick={onSelectPlan}
          title={!isAdmin ? "Only admins can change subscription plans" : undefined}
        >
          {!hasCompany ? (
            "No Company"
          ) : isProcessing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : canReactivate ? (
            "Reactivate Plan"
          ) : isCurrent ? (
            <>
              <Check className="mr-2 h-4 w-4" />
              Current Plan
            </>
          ) : !isAdmin ? (
            "Admin Only"
          ) : needsSubscription ? (
            "Subscribe"
          ) : currentTierPrice > 0 ? (
            // Show billing period upgrade if switching to yearly
            isYearlyUpgrade ? "Switch to Yearly" :
            isBillingPeriodChange ? "Switch to Monthly" :
            isUpgrade ? "Upgrade" : "Downgrade"
          ) : (
            "Get Started"
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
